import { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import {
  CheckCircle2,
  AlertCircle,
  ChevronLeft,
  MapPin,
  User,
  Calendar,
  Heart,
  Mail,
  Phone,
  CheckCircle,
  Lock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LiveRegion } from './LiveRegion';
import { ProvinceFlag } from './ProvinceFlags';

const TOTAL_STEPS = 8;

const stepMeta = [
  { label: 'Province', icon: MapPin },
  { label: 'Gender', icon: User },
  { label: 'Birth', icon: Calendar },
  { label: 'Smoker', icon: Heart },
  { label: 'Name', icon: User },
  { label: 'Email', icon: Mail },
  { label: 'Phone', icon: Phone },
  { label: 'Review', icon: CheckCircle },
];

const provinces = [
  { value: 'ON', label: 'Ontario' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'AB', label: 'Alberta' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'NS', label: 'Nova Scotia' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'NL', label: 'Newfoundland' },
  { value: 'PE', label: 'PEI' },
  { value: 'NT', label: 'NW Territories' },
  { value: 'YT', label: 'Yukon' },
  { value: 'NU', label: 'Nunavut' },
];

const provinceLabels = {
  ON: 'Ontario', BC: 'British Columbia', AB: 'Alberta', MB: 'Manitoba',
  SK: 'Saskatchewan', NS: 'Nova Scotia', NB: 'New Brunswick', NL: 'Newfoundland and Labrador',
  PE: 'Prince Edward Island', NT: 'Northwest Territories', YT: 'Yukon', NU: 'Nunavut',
};

export function FormAlt({ onSubmit }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [liveMessage, setLiveMessage] = useState('');
  const [dobError, setDobError] = useState('');
  const [birthDay, setBirthDay] = useState('');
  const [birthMonth, setBirthMonth] = useState('');
  const [birthYear, setBirthYear] = useState('');
  const formCardRef = useRef(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
    trigger,
    setValue,
  } = useForm({
    defaultValues: {
      province: '',
      gender: '',
      dateOfBirth: '',
      smoker: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
    },
  });

  const formValues = watch();

  useEffect(() => {
    const timer = setTimeout(() => {
      formCardRef.current?.querySelector('button, input, select, label')?.focus();
    }, 350);
    return () => clearTimeout(timer);
  }, [currentStep]);

  const updateDateOfBirth = useCallback((day, month, year) => {
    setDobError('');
    if (day && month && year) {
      const dob = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      setValue('dateOfBirth', dob, { shouldValidate: true });
      return true;
    }
    setValue('dateOfBirth', '', { shouldValidate: false });
    return false;
  }, [setValue]);

  const advance = useCallback((next) => {
    setDirection(1);
    setCurrentStep(next);
    setLiveMessage(`Now on step ${next} of ${TOTAL_STEPS}`);
  }, []);

  const goBack = useCallback((prev) => {
    setDirection(-1);
    setCurrentStep(prev);
    setLiveMessage(`Back to step ${prev} of ${TOTAL_STEPS}`);
  }, []);

  const handleFormSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await onSubmit(data);
    } catch (e) {
      console.error('Submit error:', e);
    }
    setIsSubmitting(false);
    setIsSuccess(true);
    setTimeout(() => {
      setIsSuccess(false);
      setCurrentStep(1);
      setBirthDay('');
      setBirthMonth('');
      setBirthYear('');
      reset();
    }, 10000);
  };

  const goToNextStep = async (fields) => {
    const ok = await trigger(fields);
    if (ok) {
      advance(currentStep + 1);
    } else {
      setLiveMessage('Please complete all required fields');
    }
  };

  const sel = 'border-warm-700 bg-warm-50 ring-2 ring-warm-400/50 shadow-sm';
  const unsel = 'border-warm-200 bg-white hover:border-warm-300 hover:bg-warm-50/50';
  const errCls = 'text-red-600 text-xs mt-1.5 font-medium flex items-center gap-1';
  const inputCls = 'h-12 border-warm-200 bg-white focus:border-warm-600 focus:ring-2 focus:ring-warm-200 rounded-lg text-warm-900 placeholder:text-warm-400';
  const selectCls = 'w-full h-12 text-sm px-3 rounded-lg border border-warm-200 focus:border-warm-600 focus:outline-none focus:ring-2 focus:ring-warm-200 bg-white text-warm-900';

  const progressPct = Math.round(((currentStep - 1) / TOTAL_STEPS) * 100);

  const MicroFeedback = ({ message }) => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex items-center gap-1.5 mt-3 text-[11px] text-warm-500"
    >
      <CheckCircle2 className="w-3 h-3 text-warm-400" />
      {message}
    </motion.div>
  );

  const slideVariants = {
    enter: (d) => ({ x: d > 0 ? 40 : -40, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (d) => ({ x: d > 0 ? -40 : 40, opacity: 0 }),
  };

  const StepHeader = ({ step, title, subtitle }) => {
    const Icon = stepMeta[step - 1].icon;
    return (
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-full bg-warm-900 flex items-center justify-center flex-shrink-0">
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="font-bold text-warm-950 text-base">{title}</h3>
          <p className="text-xs text-warm-500">{subtitle}</p>
        </div>
      </div>
    );
  };

  const BackButton = ({ to }) => (
    <button
      type="button"
      onClick={() => goBack(to)}
      className="flex items-center gap-1 text-xs text-warm-400 hover:text-warm-600 transition-colors mt-4"
      data-testid="form-back-btn"
    >
      <ChevronLeft className="w-3.5 h-3.5" /> Back
    </button>
  );

  const ContinueRow = ({ onContinue, backTo }) => (
    <div className="flex gap-2.5 pt-2">
      <Button type="button" onClick={() => goBack(backTo)} variant="outline" className="h-11 px-4 font-semibold border-warm-200 text-warm-600 hover:bg-warm-50 rounded-xl">
        <ChevronLeft className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        onClick={onContinue}
        data-testid="form-continue-btn"
        className="flex-1 h-11 font-semibold bg-warm-700 hover:bg-warm-800 text-white rounded-xl transition-colors"
      >
        Continue
      </Button>
    </div>
  );

  if (isSuccess) {
    return (
      <div aria-live="polite" data-testid="form-success-inline">
        <LiveRegion message="Form submitted successfully!" role="alert" />
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white border border-warm-200 rounded-2xl p-6 text-center shadow-sm"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 300, delay: 0.2 }}
            className="w-14 h-14 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4"
          >
            <CheckCircle2 className="w-8 h-8 text-white" />
          </motion.div>
          <h2 className="text-2xl font-bold text-warm-950 mb-2">
            Thank You, {formValues.firstName || 'there'}!
          </h2>
          <p className="text-sm text-warm-600 mb-5">
            Your details have been submitted successfully. A licensed <strong>Seniors Choice</strong> advisor will be in touch shortly to discuss your coverage options.
          </p>
          <div className="bg-warm-50 border border-warm-100 rounded-xl p-4 text-left space-y-2.5 text-sm text-warm-700">
            <p className="font-semibold text-warm-900 text-xs uppercase tracking-wide">What happens next</p>
            {[
              'Expect a phone call from a licensed advisor',
              'They will ask a few simple health and lifestyle questions',
              'Your personalized coverage options will be provided instantly',
            ].map((t, i) => (
              <div key={i} className="flex gap-2.5 items-start">
                <span className="w-5 h-5 rounded-full bg-warm-200 text-warm-700 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
                <p className="text-warm-700">{t}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="lead-capture-form">
      {/* Progress percentage + Segmented step progress */}
      {currentStep > 1 && (
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10px] text-warm-500 font-medium">{progressPct}% complete</span>
          <span className="text-[10px] text-warm-400">Step {currentStep} of {TOTAL_STEPS}</span>
        </div>
      )}
      <div className="mb-5" role="navigation" aria-label="Form progress">
        {/* Mobile: simple bar */}
        <div className="block sm:hidden">
          <div className="w-full h-2 bg-warm-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-warm-700 rounded-full transition-all duration-500"
              style={{ width: `${Math.max(((currentStep - 1) / TOTAL_STEPS) * 100, 5)}%` }}
            />
          </div>
        </div>
        {/* Desktop: segmented with labels */}
        <div className="hidden sm:flex items-center gap-1 sm:gap-1.5">
          {stepMeta.map((meta, i) => {
            const step = i + 1;
            const isComplete = currentStep > step;
            const isCurrent = currentStep === step;
            return (
              <div key={meta.label} className="flex-1 flex flex-col items-center gap-1.5">
                <button
                  type="button"
                  disabled={step > currentStep}
                  onClick={() => { if (step < currentStep) goBack(step); }}
                  className={`w-full h-2 rounded-full transition-all duration-500 ${
                    isComplete
                      ? 'bg-warm-800 cursor-pointer hover:bg-warm-900'
                      : isCurrent
                      ? 'bg-warm-600'
                      : 'bg-warm-200'
                  }`}
                  aria-label={`Step ${step}: ${meta.label}${isComplete ? ' (complete)' : isCurrent ? ' (current)' : ''}`}
                />
                <span
                  className={`text-[9px] sm:text-[11px] transition-colors duration-300 whitespace-nowrap ${
                    isCurrent ? 'text-warm-900 font-bold' : isComplete ? 'text-warm-600 font-medium' : 'text-warm-300'
                  }`}
                >
                  {meta.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Card */}
      <div ref={formCardRef} className="bg-white border-2 border-warm-200 rounded-2xl p-5 sm:p-7 shadow-lg overflow-hidden">
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <AnimatePresence mode="wait" custom={direction}>

            {/* STEP 1 - Province */}
            {currentStep === 1 && (
              <motion.div key="s1" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.25, ease: 'easeInOut' }}>
                <StepHeader step={1} title="What province do you live in?" subtitle="Tap your province to continue" />

                <input type="hidden" {...register('province', { required: 'Please select your province', validate: (v) => v !== '' || 'Please select your province' })} />

                <div className="grid grid-cols-3 min-[400px]:grid-cols-4 sm:grid-cols-4 gap-2.5 sm:gap-3">
                  {provinces.map((prov) => {
                    const active = formValues.province === prov.value;
                    return (
                      <motion.button
                        key={prov.value}
                        type="button"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          setValue('province', prov.value, { shouldValidate: true });
                          setTimeout(() => advance(2), 200);
                        }}
                        data-testid={`province-${prov.value}`}
                        className={`relative flex flex-col items-center gap-1 p-2.5 sm:p-3 border-2 rounded-xl cursor-pointer transition-all focus-visible:outline-2 focus-visible:outline-warm-600 focus-visible:outline-offset-2 ${
                          active ? 'border-warm-800 bg-warm-50 ring-2 ring-warm-400/50 shadow-md' : 'border-warm-200 bg-white hover:border-warm-400 hover:shadow-sm hover:bg-warm-50/50'
                        }`}
                        aria-pressed={active}
                        aria-label={`Select ${prov.label}`}
                      >
                        {active && (
                          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-warm-800 rounded-full flex items-center justify-center z-10 shadow">
                            <CheckCircle2 className="w-3 h-3 text-white" />
                          </motion.div>
                        )}
                        <div className="w-full aspect-[3/2] rounded-lg overflow-hidden border border-warm-100 shadow-sm">
                          <ProvinceFlag code={prov.value} className="w-full h-full object-cover" />
                        </div>
                        <span className="text-[10px] sm:text-xs font-bold text-warm-900 leading-none mt-1">{prov.label}</span>
                        <span className="text-[9px] sm:text-[10px] text-warm-400 leading-tight">{prov.value}</span>
                      </motion.button>
                    );
                  })}
                </div>

                <p className="text-[10px] text-warm-400 mt-3">* Coverage is not available in Quebec.</p>

                {errors.province && (
                  <p className={`${errCls} mt-2`}><AlertCircle className="w-3 h-3" />{errors.province.message}</p>
                )}
              </motion.div>
            )}

            {/* STEP 2 - Gender */}
            {currentStep === 2 && (
              <motion.div key="s2" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.25, ease: 'easeInOut' }}>
                <StepHeader step={2} title="What is your gender?" subtitle="This helps to calculate accurate rates" />

                <div className="grid grid-cols-2 gap-2">
                  {['Male', 'Female'].map((g) => {
                    const active = formValues.gender === g;
                    return (
                      <motion.button
                        key={g}
                        type="button"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={() => {
                          setValue('gender', g, { shouldValidate: true });
                          setTimeout(() => advance(3), 200);
                        }}
                        data-testid={`gender-${g.toLowerCase()}`}
                        className={`flex items-center justify-center h-14 border rounded-xl cursor-pointer transition-colors focus-visible:outline-2 focus-visible:outline-warm-600 ${
                          active ? sel : unsel
                        }`}
                        aria-pressed={active}
                      >
                        <span className="text-sm font-semibold text-warm-900">{g}</span>
                        {active && (
                          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="ml-2">
                            <CheckCircle2 className="w-4 h-4 text-warm-700" />
                          </motion.div>
                        )}
                      </motion.button>
                    );
                  })}
                </div>

                <input type="hidden" {...register('gender', { required: 'Required' })} />
                {errors.gender && <p className={`${errCls} mt-2`}><AlertCircle className="w-3 h-3" />{errors.gender.message}</p>}

                {formValues.gender && <MicroFeedback message="Great choice!" />}

                <BackButton to={1} />
              </motion.div>
            )}

            {/* STEP 3 - Date of Birth */}
            {currentStep === 3 && (
              <motion.div key="s3" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.25, ease: 'easeInOut' }}>
                <StepHeader step={3} title="What is your date of birth?" subtitle="You must be between 50-80 years old to qualify" />

                <input type="hidden" {...register('dateOfBirth', { required: 'Required' })} />
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-[10px] font-semibold text-warm-500 mb-1 uppercase tracking-wide">Day</label>
                    <select
                      value={birthDay}
                      onChange={(e) => {
                        setBirthDay(e.target.value);
                        const complete = updateDateOfBirth(e.target.value, birthMonth, birthYear);
                        if (complete) setTimeout(() => advance(4), 300);
                      }}
                      className={selectCls}
                      aria-label="Birth day"
                      data-testid="birth-day-select"
                    >
                      <option value="">DD</option>
                      {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                        <option key={d} value={String(d).padStart(2, '0')}>{d}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-warm-500 mb-1 uppercase tracking-wide">Month</label>
                    <select
                      value={birthMonth}
                      onChange={(e) => {
                        setBirthMonth(e.target.value);
                        const complete = updateDateOfBirth(birthDay, e.target.value, birthYear);
                        if (complete) setTimeout(() => advance(4), 300);
                      }}
                      className={selectCls}
                      aria-label="Birth month"
                      data-testid="birth-month-select"
                    >
                      <option value="">MM</option>
                      {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => (
                        <option key={m} value={String(i + 1).padStart(2, '0')}>{m}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-warm-500 mb-1 uppercase tracking-wide">Year</label>
                    <select
                      value={birthYear}
                      onChange={(e) => {
                        setBirthYear(e.target.value);
                        const complete = updateDateOfBirth(birthDay, birthMonth, e.target.value);
                        if (complete) setTimeout(() => advance(4), 300);
                      }}
                      className={selectCls}
                      aria-label="Birth year"
                      data-testid="birth-year-select"
                    >
                      <option value="">YYYY</option>
                      {Array.from({ length: 31 }, (_, i) => 1976 - i).map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {(errors.dateOfBirth || dobError) && (
                  <p className={errCls}><AlertCircle className="w-3 h-3" />{dobError || errors.dateOfBirth?.message}</p>
                )}

                <BackButton to={2} />
              </motion.div>
            )}

            {/* STEP 4 - Smoking Status */}
            {currentStep === 4 && (
              <motion.div key="s4" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.25, ease: 'easeInOut' }}>
                <StepHeader step={4} title="Do you currently smoke?" subtitle="This affects your premium rate" />

                <div className="space-y-2">
                  {[
                    { val: 'no', label: "No, I don't smoke" },
                    { val: 'yes', label: 'Yes, I smoke' },
                  ].map(({ val, label }) => {
                    const active = formValues.smoker === val;
                    return (
                      <motion.button
                        key={val}
                        type="button"
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => {
                          setValue('smoker', val, { shouldValidate: true });
                          setTimeout(() => advance(5), 200);
                        }}
                        data-testid={`smoker-${val}`}
                        className={`w-full flex items-center gap-3 p-3.5 border rounded-xl cursor-pointer transition-colors focus-visible:outline-2 focus-visible:outline-warm-600 ${
                          active ? sel : unsel
                        }`}
                        aria-pressed={active}
                      >
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${active ? 'bg-warm-700 text-white' : 'bg-warm-100 text-warm-500'}`}>
                          <Heart className="w-5 h-5" />
                        </div>
                        <span className="text-sm font-semibold text-warm-950">{label}</span>
                        {active && (
                          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="ml-auto">
                            <CheckCircle2 className="w-5 h-5 text-warm-700 flex-shrink-0" />
                          </motion.div>
                        )}
                      </motion.button>
                    );
                  })}
                </div>

                <input type="hidden" {...register('smoker', { required: 'Required' })} />
                {errors.smoker && <p className={`${errCls} mt-2`}><AlertCircle className="w-3 h-3" />{errors.smoker.message}</p>}

                {formValues.smoker === 'no' && (
                  <MicroFeedback message="Great news — non-smokers typically qualify for the best rates" />
                )}
                {formValues.smoker === 'yes' && (
                  <MicroFeedback message="No problem — smokers can still qualify for coverage up to $25,000" />
                )}

                <BackButton to={3} />
              </motion.div>
            )}

            {/* STEP 5 - Name */}
            {currentStep === 5 && (
              <motion.div key="s5" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.25, ease: 'easeInOut' }} className="space-y-3">
                <StepHeader step={5} title="What is your name?" subtitle="We'll use this to personalise your coverage options" />

                <div className="grid grid-cols-1 min-[400px]:grid-cols-2 gap-2.5">
                  <div>
                    <label htmlFor="firstName" className="block text-xs font-semibold text-warm-700 mb-1">First Name</label>
                    <Input id="firstName" placeholder="Jane" className={inputCls} data-testid="input-first-name" {...register('firstName', { required: 'Required', minLength: { value: 2, message: 'Min 2 chars' } })} />
                    {errors.firstName && <p className={errCls}><AlertCircle className="w-3 h-3" />{errors.firstName.message}</p>}
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-xs font-semibold text-warm-700 mb-1">Last Name</label>
                    <Input id="lastName" placeholder="Smith" className={inputCls} data-testid="input-last-name" {...register('lastName', { required: 'Required', minLength: { value: 2, message: 'Min 2 chars' } })} />
                    {errors.lastName && <p className={errCls}><AlertCircle className="w-3 h-3" />{errors.lastName.message}</p>}
                  </div>
                </div>

                <ContinueRow onContinue={() => goToNextStep(['firstName', 'lastName'])} backTo={4} />
              </motion.div>
            )}

            {/* STEP 6 - Email */}
            {currentStep === 6 && (
              <motion.div key="s6" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.25, ease: 'easeInOut' }} className="space-y-3">
                <StepHeader step={6} title="What is your email address?" subtitle="We'll send your eligibility confirmation here" />

                <div>
                  <Input
                    id="email"
                    type="email"
                    placeholder="jane@email.com"
                    className={inputCls}
                    data-testid="input-email"
                    {...register('email', {
                      required: 'Required',
                      pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: 'Invalid email' },
                    })}
                  />
                  {errors.email && <p className={errCls}><AlertCircle className="w-3 h-3" />{errors.email.message}</p>}
                </div>

                <div className="flex items-start gap-2 bg-warm-50 border border-warm-100 rounded-lg p-3">
                  <Lock className="w-3.5 h-3.5 text-warm-400 flex-shrink-0 mt-0.5" />
                  <p className="text-[10px] text-warm-500 leading-relaxed">
                    Your email will be shared with <strong className="text-warm-700">Seniors Choice</strong>, a licensed insurance advisor. No spam, no third parties — ever.
                  </p>
                </div>

                <ContinueRow onContinue={() => goToNextStep(['email'])} backTo={5} />
              </motion.div>
            )}

            {/* STEP 7 - Phone */}
            {currentStep === 7 && (
              <motion.div key="s7" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.25, ease: 'easeInOut' }} className="space-y-3">
                <StepHeader step={7} title="What is your phone number?" subtitle="A licensed Seniors Choice advisor will call you to discuss your coverage" />

                <div>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="(555) 123-4567"
                    className={inputCls}
                    data-testid="input-phone"
                    {...register('phone', {
                      required: 'Required',
                      pattern: { value: /^[\d\s\-()]+$/, message: 'Invalid phone' },
                      minLength: { value: 10, message: 'Too short' },
                    })}
                  />
                  {errors.phone && <p className={errCls}><AlertCircle className="w-3 h-3" />{errors.phone.message}</p>}
                </div>

                <div className="flex items-start gap-2 bg-warm-50 border border-warm-100 rounded-lg p-3">
                  <Phone className="w-3.5 h-3.5 text-warm-400 flex-shrink-0 mt-0.5" />
                  <p className="text-[10px] text-warm-500 leading-relaxed">
                    Your advisor will call <strong className="text-warm-700">once</strong>, at a time that works for you. No spam, no pressure, no telemarketing — ever.
                  </p>
                </div>

                <ContinueRow onContinue={() => goToNextStep(['phone'])} backTo={6} />
              </motion.div>
            )}

            {/* STEP 8 - Review & Submit */}
            {currentStep === 8 && (
              <motion.div key="s8" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.25, ease: 'easeInOut' }} className="space-y-3">
                <StepHeader step={8} title="Review Your Information" subtitle="Please confirm your details are correct" />

                <div className="bg-warm-50 border border-warm-100 rounded-xl divide-y divide-warm-100">
                  {[
                    { label: 'Province', value: provinceLabels[formValues.province] || formValues.province, step: 1 },
                    { label: 'Gender', value: formValues.gender, step: 2 },
                    { label: 'Date of Birth', value: formValues.dateOfBirth ? new Date(formValues.dateOfBirth + 'T00:00:00').toLocaleDateString('en-CA', { day: 'numeric', month: 'long', year: 'numeric' }) : '', step: 3 },
                    { label: 'Smoking Status', value: formValues.smoker === 'no' ? "Non-smoker" : formValues.smoker === 'yes' ? "Smoker" : '', step: 4 },
                    { label: 'Name', value: `${formValues.firstName} ${formValues.lastName}`.trim(), step: 5 },
                    { label: 'Email', value: formValues.email, step: 6 },
                    { label: 'Phone', value: formValues.phone, step: 7 },
                  ].map((row) => (
                    <div key={row.label} className="flex items-center justify-between px-3.5 py-2.5" data-testid={`review-${row.label.toLowerCase().replace(/\s/g, '-')}`}>
                      <div>
                        <p className="text-[10px] text-warm-400 uppercase tracking-wide">{row.label}</p>
                        <p className="text-sm font-semibold text-warm-900">{row.value}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => goBack(row.step)}
                        className="text-[10px] text-warm-500 hover:text-warm-700 underline transition-colors"
                      >
                        Edit
                      </button>
                    </div>
                  ))}
                </div>

                <div className="space-y-2">
                  <p className="text-[10px] text-warm-500 leading-relaxed">
                    By clicking "Check My Eligibility", you consent to be contacted by <strong className="text-warm-700">Seniors Choice</strong> (or their representatives) via phone, email, or SMS regarding your insurance inquiry. Your phone number will only be used to contact you about your coverage inquiry — no marketing calls, no third parties.
                  </p>
                  <p className="text-[10px] text-warm-400 leading-relaxed italic">
                    Canada Care is an introducer service only — we are not an insurance company, broker, or advisor and do not provide insurance cover or advice. We connect you to Seniors Choice, a licensed insurance provider. Final availability is subject to health and lifestyle questions asked during your phone application with a Seniors Choice advisor. No coverage is in place until a policy has been formally issued.
                  </p>
                </div>

                <div className="flex gap-2.5">
                  <Button type="button" onClick={() => goBack(7)} variant="outline" className="h-12 px-4 font-semibold border-warm-200 text-warm-600 hover:bg-warm-50 rounded-xl">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="form-submit-btn"
                    className="flex-1 h-12 font-bold bg-warm-700 hover:bg-warm-800 text-white rounded-xl transition-colors disabled:opacity-60"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Checking...
                      </span>
                    ) : (
                      'Check My Eligibility'
                    )}
                  </Button>
                </div>

                <div className="flex items-center justify-center gap-1.5 mt-2">
                  <Lock className="w-3 h-3 text-warm-400" />
                  <p className="text-[10px] text-warm-400">256-bit SSL encrypted &middot; Your data is secure</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </form>
      </div>

      <LiveRegion message={liveMessage} />
    </div>
  );
}
